import { Component } from "./Component.js";
import { Router } from "./Router.js";
import { StatusOverlay } from "./components/StatusOverlay.js";
import { Toaster } from "./components/Toaster.js";
import { Logger } from "../tracing/Logger.js";
import { Menu } from "./controls/Menu.js";
import * as http from "../utils/http.js";
import { isFunction } from "../data/utils.js";
import { getAuthSuccessRedirect } from "./application-auth-helpers.js";
import { ApplicationServices } from "./ApplicationServices.js";

/**
 * @param {AuthManager} authManager - Autentizační manager.
 * @param {Array<RouteDef>} routes - Definice aplikačních rout.
 * @param {Array<RouteDef>} defaultRoutes - Jména speciálních rout. Musí odpovídat jménům v routes.
 */
export class Application extends Component {

    constructor(options = {}) {

        let {
            authManager = null,
            authService = null,
            httpService = null,
            serviceFactory = null,
            routes,
            defaultRoutes,
            mainMenuDefs = [],
            config = null,
            i18n = null,
            storage = null,
            logger = null,
            renderer = null,
            services = null,
        } = options;

        services = services || new ApplicationServices({
            authManager,
            authService,
            httpService,
            serviceFactory,
            config,
            i18n,
            storage,
            logger,
            renderer,
        });

        authManager = authManager || services.getAuthManager?.() || null;
        authService = authService || services.getAuthService?.() || null;
        httpService = httpService || services.getHttpService?.() || null;
        serviceFactory = serviceFactory || services.getServiceFactory?.() || null;
        config = config || services.getConfig?.() || null;
        i18n = i18n || services.getI18n?.() || null;
        storage = storage || services.getStorage?.() || null;
        logger = logger || services.getLogger?.() || null;
        renderer = renderer || services.getRenderer?.() || null;

        if (!authManager) throw new Error('[Application] "authManager" is required.');
        if (!routes) throw new Error('[Application] "routes" is required.');
        if (!defaultRoutes) throw new Error('[Application] "defaultRoutes" is required.');
        if (!config) throw new Error('[Application] "config" is required.');
        if (!i18n) throw new Error('[Application] "i18n" is required.');
        if (!storage) throw new Error('[Application] "storage" is required.');
        if (!logger) throw new Error('[Application] "logger" is required.');

        super({});

        this.services = services;
        this.config = config;
        this.i18n = i18n;
        this.storage = storage;
        this.logger = logger;
        this.renderer = renderer;

        this.logger.onLog((e) => {

            switch (e.level) {
                case Logger.ERROR:
                    this.toaster.error(e.message, null, { details: e.data });
                    break;
                case Logger.WARNING:
                    this.toaster.warning(e.message, null, { details: e.data });
                    break;
                case Logger.SUCCESS:
                    this.toaster.success(e.message, null, { details: e.data });
                    break;
                default:
                    this.toaster.info(e.message, null, { details: e.data });
                    break;
            }

        });

        this.router = new Router({
            app: this,
            authManager,
            routes,
            defaultRoutes,
            onRouteChange: (routeMatch) => this._handleRouteChange(routeMatch),
            defaultRouteName: defaultRoutes.defaultRouteName,
            loginRouteName: defaultRoutes.loginRouteName,
            forbiddenRouteName: defaultRoutes.forbiddenRouteName,
            notFoundRouteName: defaultRoutes.notFoundRouteName,
        });

        this.toaster = new Toaster(this, {
            toastTimeOut: 5000,
            position: Toaster.POSITION_BOTTOM_RIGHT,
        });

        this.statusOverlay = new StatusOverlay(this);

        this.activeModule = null;
        this.activeModuleKey = null;

        this.authManager = authManager;
        this.authService = authService;
        this.httpService = httpService;
        this.serviceFactory = serviceFactory;
        this.mainMenuDefs = mainMenuDefs || [];
        authManager.onLogin((ev) => {
            if (ev?.remote) {
                this.mainMenu.refresh();
                this.invalidate();
                return;
            }
            this._handleAuthSuccess();
        });
        authManager.onLogout((ev) => { this._handleAuthFailure(); });
        authManager.onAuthError((error) => {
            if (!this.authManager.isAuthenticated()) {
                this._handleAuthFailure();
                return;
            }
            this.mainMenu.refresh();
            this.invalidate();
        });
        authManager.onTokenRefreshed((ev) => {
            if (!ev?.remote) {
                return;
            }
            this.mainMenu.refresh();
            this.invalidate();
        });

        // DO NOT use parent autoregistration - special rendering position
        this.mainMenu = new Menu(null, { items: () => this.getMainMenuDefs() });
        this.mainMenu.parent = this;

        this.dataStorage = storage;

        this.$initialized = true;

    };

    isInitialized() {

        return this.$initialized;

    };

    async prepare() {

        await this.mainMenu.prepare();
        await this.mainMenu.refresh();

    };

    async refresh() {

        await this.mainMenu.refresh();

    };

    // --------------------------------------------------------------------------------------------

    getParent() { return null; };

    getApp() { return this; };

    getServices() { return this.services; };

    getAuthManager() { return this.getServices().getAuthManager(); };

    getAuthService() { return this.getServices().getAuthService(); };

    getHttpService() { return this.getServices().getHttpService(); };

    getServiceFactory() { return this.getServices().getServiceFactory(); };

    getConfig() { return this.getServices().getConfig(); };

    getI18n() { return this.getServices().getI18n(); };

    getStorage() { return this.getServices().getStorage(); };

    getLogger() { return this.getServices().getLogger(); };

    getRenderer() { return this.getServices().getRenderer(); };

    async prepareDataModel(dataModel) {

        if (!dataModel)
            return null;

        if (typeof dataModel.setCodelistValueLoader === 'function') {
            const serviceFactory = this.getServiceFactory();
            if (serviceFactory && typeof serviceFactory.createTypeCodelistValueLoader === 'function')
                dataModel.setCodelistValueLoader(serviceFactory.createTypeCodelistValueLoader());
        }

        if (typeof dataModel.prepare === 'function')
            await dataModel.prepare();

        return dataModel;

    };

    invalidate(level = 0) { this._rendererView?.redraw(); }

    // --------------------------------------------------------------------------------------------

    getAppLogo() { return '/img/app-logo.png'; };

    getAppName() { return 'Application'; };

    getAvailableLocales() {

        return [
            {
                code: 'en',
                name: 'English'
            },
            {
                code: 'en-gb',
                name: 'English (Great Britain)'
            },
            {
                code: 'cs',
                name: 'České'
            },
        ];

    };

    getLocale() {

        const config = this.getConfig();
        return ((typeof this.locale == 'function') ? this.locale.call(this) : this.locale) || config.locale;

    };

    async setLocale(locale) {

        this.setBusyFlag();
        try {
            const changed = await this.getI18n().setLocale(locale, true);
            if (changed)
                http.setCookie('locale', locale);
            this.invalidate();
        } catch (err) {
            this.handleError(err)
        } finally {
            this.clearBusyFlag();
        }

    };

    // --------------------------------------------------------------------------------------------

    isApplicationBusy() { return this.statusOverlay.isVisible(); };

    setBusyFlag() { this.statusOverlay.show(); };

    clearBusyFlag() { this.statusOverlay.hide(); };

    // --------------------------------------------------------------------------------------------

    getMainMenuDefs() {

        return this.mainMenuDefs || [];

    };

    // -------------------------------------------------------------------------------------------- Route hash & URL helpers

    getModuleUrl(modulePath) { return this.getConfig().baseUrl + '#' + modulePath; };

    getApiUrl(apiPath, params) { return this.getConfig().baseUrl + '/api/' + apiPath; };

    getRoute(routeName) { return this.router._findRoute(routeName); };

    getRouteDef(routeName) { return this.getRoute()?.def; }

    getRouteHash(routeName, params = {}, query = {}) { return this.router.getRouteHash(routeName, params, query); };

    getRouteUrl(routeName, params, query) { return this.router.getRouteUrl(routeName, params, query); };

    getActiveRoute() { return this.router.getActiveRoute(); };

    getActiveRouteDef() { return this.getActiveRoute()?.def; };

    getActiveRouteName() { return this.getActiveRoute()?.def?.name; };

    getActiveRouteParams() { return this.getActiveRoute()?.params; };

    getActiveRouteQuery() { return this.getActiveRoute()?.query; };

    // --------------------------------------------------------------------------------------------

    getAcitveModule() { return this.activeModule; };

    moduleChanged(module) { /* do nothing */ };

    // -------------------------------------------------------------------------------------------- Navigation & Routing

    /**
     * Changes nav, creates new nav history record.
     */
    navigateTo(url) { this.router.navigateTo(url); };

    navigateBack() { this.router.navigateBack(); };

    routeTo(routeName, params = {}, query = {}) { return this.router.routeTo(routeName, params, query); };

    routeToDefaultRoute() {

        this.mainMenu.active = false;
        this.router.routeToDefault();

    };

    // --------------------------------------------------------------------------------------------

    _handleAuthSuccess() {

        this.mainMenu.refresh();

        const routeRedirect = getAuthSuccessRedirect(this.getActiveRouteQuery());

        if (routeRedirect) {
            this.router.navigateToHash(routeRedirect);
            return;
        }

        this.routeToDefaultRoute();

    };

    _handleAuthFailure() {

        this.mainMenu.refresh();
        this.routeToDefaultRoute();

    };

    // -------------------------------------------------------------------------------------------- Routing handlers

    async _handleRouteChange(routeMatch) {

        this.setBusyFlag();
        try {

            if (routeMatch.notFound) {
                this.activeModule = null;
                this.invalidate();
                return;
            }

            const routeDef = routeMatch.def;

            if (!routeDef) {
                this.activeModule = null;
                this.invalidate();
                return;
            }

            let moduleKey = routeDef.name;

            if (moduleKey != this.activeModuleKey) {

                try {

                    const moduleInstance = await this._createModuleFromRouteDef(routeDef, routeMatch);
                    moduleInstance.parent = this;

                    this.activeModule = moduleInstance;
                    this.activeModuleKey = moduleKey;

                    if (isFunction(this.activeModule.prepare)) {
                        const activeRoute = routeMatch;
                        const routeOptions = activeRoute.def?.options ?? {};
                        await this.activeModule.prepare(routeOptions, activeRoute);
                    }

                    this.mainMenu.hide();
                    this.mainMenu.activateNodeByRoute(routeDef.name);

                } catch (err) {

                    console.error('Failed to load module for route:', routeDef.name, err);
                    this.handleError(err);

                    this.activeModule = null;
                    this.activeModuleKey = null;

                    this.invalidate();

                    return;

                }

            }

            if (this.activeModule && typeof this.activeModule.refresh === 'function') {
                const activeRoute = routeMatch;
                const routeOptions = activeRoute.def?.options ?? {};
                await this.activeModule.refresh(routeOptions, activeRoute);
            }

            this.invalidate();

        } finally {

            this.clearBusyFlag();

        }

    }

    async _createModuleFromRouteDef(routeDef, routeMatch) {

        const routeFactory = routeDef?.factory ?? null;
        const routeOptions = routeDef?.options ?? {};

        if (typeof routeFactory !== 'function')
            throw new Error(`[Application] Route "${routeDef?.name ?? '?'}" is missing factory callback.`);

        return await routeFactory(this, routeDef, routeMatch, routeOptions);

    }

    // --------------------------------------------------------------------------------------------

    showToast(type, message, description) {

        switch (type) {
            case 'error':
                this.toaster.error(message, description);
                break;
            case 'warning':
                toaster.warning(message, description);
                break;
            case 'success':
                this.toaster.success(message, description);
                break;
            default:
                this.toaster.info(message, description);
        }

    };

    handleError(error) {

        if (!error) {
            this.logger.error(`Uknown error occured.`);
            return;
        }

        if (error.type === 'error' && error.payload) {

            const payload = error.payload;
            const errorText = `[${payload.code ?? error.status ?? '?'}] ${payload.description ?? payload.message ?? 'Unknown error.'}`;
            this.logger.error(errorText, payload.details ?? null);
            return;
        }

        if (error.envelope?.type === 'error' && error.envelope.payload) {

            const payload = error.envelope.payload;
            const errorText = `[${payload.code ?? error.envelope.status ?? error.status ?? '?'}] ${payload.description ?? payload.message ?? 'Unknown error.'}`;
            this.logger.error(errorText, payload.details ?? null);
            return;
        }

        if (error.details) {

            const innerError = error.details;

            const errorText = `[${innerError.code ?? '?'}] ${innerError.description ?? innerError.message}`;
            let errorData = null;

            if (innerError.details) {
                if (innerError.details.errors) {
                    errorData = Object.values(innerError.details.errors);
                } else if (innerError.details.fields) {
                    errorData = innerError.details.fields;
                }
            }

            this.logger.error(errorText, errorData);
            return;
        }

        if (error.error && error.error.code && error.error.message) {
            this.logger.error(`[${error.error.code}] ${error.error.message}`);
            return;
        }
        if (error.code && error.message) {
            this.logger.error(`[${error.code}] ${error.message}`);
            return;
        }
        if (error.message) {
            this.logger.error(error.message);
            return;
        }
        this.logger.error(error);

    };

    processApiError(error) {

        this.handleError(error);
        if (error.code == 401) {
            this.authManager.clearLoggedUser();
            //this.routeTo('auth-login');
            return;
        }

    };

    // --------------------------------------------------------------------------------------------

    _render(r, vm) {

        const i18n = this.getI18n();
        const app = this;

        let activeModule = this.getAcitveModule(),
            appLogo = app.getAppLogo() || '/img/app-logo.png',
            appName = app.getAppName() || 'Application',
            loggedUser = this.authManager.getUser();

        return r.el('div', 'app-container', null,
            [
                r.el('div', 'app-navbar', null, [
                    r.header(null, [
                        r.nav(null, [
                            r.link({ url: '#', css: 'p-2' }, [
                                r.img({ props: { src: appLogo } }),
                            ]),
                            ' ',
                            r.link({ url: '#', css: 'p-2' }, [
                                appName
                            ]),
                            r.spacer(),
                            ...renderLangControls(),
                            ...renderUserControls(),
                        ]),
                    ]),
                ]),
                r.el('div', 'grid-x app-content off-canvas off-canvas-sidebar-show', null, [
                    r.el('div', 'app-sidebar off-canvas-sidebar', null, [
                        '[Side Bar]'
                    ]),
                    r.el('div', 'grid-y app-module off-canvas-content', null, [
                        r.el('div', null, null, [
                            activeModule ? activeModule.render(r, vm) : 'No active module'
                        ])
                    ]),
                    r.el('a', 'off-canvas-overlay', { href: '#close' }),
                ]),
                ...app.controls.map((control) => { return control.render(r, vm); })
            ]
        );

        function renderLangControls() {

            return [
                r.icon('#language', 'mr-2'),
                ' ',
                r.comboBox(app.getLocale(), {
                    props: {
                        style: 'max-width:10rem;',
                        onchange: (e) => {
                            app.setLocale(e?.target?.value);
                        }
                    },
                    items: app.getAvailableLocales().map((def) => {
                        return {
                            label: def.name,
                            value: def.code,
                            data: def
                        };
                    }),
                })
            ];

        };

        function renderUserControls() {

            const loginUrl = app.getRouteUrl('auth-login');

            if (!loggedUser) {

                return [
                    r.link({ url: loginUrl, css: 'button' }, [r.icon('#login'), ' ', i18n.t('app.label_login')]),
                ];

            }

            const accountUrl = app.getRouteUrl('account-details'),
                logoutUrl = app.getRouteUrl('auth-logout'),
                userName = (loggedUser.firstName || '') + ' ' + (loggedUser.lastName || '').toUpperCase(),
                userUsername = loggedUser.username,
                userDisplayName = userName != ' ' ? userName : userUsername;

            return [
                r.link({ url: accountUrl, css: 'button' }, [r.icon('#user'), ' ', userDisplayName]),
                ' ',
                r.link({ url: logoutUrl, css: 'button' }, [r.icon('#logout'), ' ', i18n.t('app.label_logout')]),
            ];

        };

    };

    // --------------------------------------------------------------------------------------------

    async start(rootElement, options = {}) {

        if (!rootElement)
            throw new Error('[Application] "rootElement" is required.');

        await this.prepare();

        const {
            consumeRootElement = true
        } = options;

        this._rootElement = rootElement;

        const renderer = this.getRenderer();
        this._rendererView = renderer.createView((vm, app) => () => app._render(renderer, vm), this);
        this._rendererView.mount(rootElement, consumeRootElement);

        this.router.start();

        this.invalidate();

    };


}
